import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"
import { em, hideVisually } from "polished"
import {
  Meta,
  Search,
  SearchResult,
} from "@outdoormap/gatsby-plugin-brand-sites"

import Layout from "../components/layout"
import SearchForm from "../components/search-form"
import { Heading1, Heading3 } from "../components/styled/heading"
import { Paragraph, ParagraphSmall } from "../components/styled/paragraph"

export default function SearchTemplate({
  params: { "*": query },
  data: { site, translations, image },
}) {
  const [inputValue, setInputValue] = useState(query)

  useEffect(() => {
    setInputValue(query)
  }, [query])

  return (
    <Layout>
      {image && (
        <Images>
          <GatsbyImage
            image={image.childImageSharp.gatsbyImageData}
            alt={translations.search.title}
          />
        </Images>
      )}

      <Container>
        <Heading1>{translations.search.title}</Heading1>

        <SearchFormWrap>
          <SearchForm
            inputProps={{
              value: inputValue,
              placeholder: `${translations.search.title} ${site.siteMetadata.name}`,
              onChange: (e) => setInputValue(e.target.value),
            }}
          />
        </SearchFormWrap>

        <Search query={query}>
          {({ status, total, results, page, hasNextPage, showNextPage }) => (
            <>
              {status == `results` && (
                <Info>
                  <strong>{total}</strong>
                  {` `}
                  {translations.search.results?.toLowerCase()}
                </Info>
              )}

              {status == `searching` && (
                <Loader>
                  <span>{translations.search.loadingResults}</span>

                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} />
                  ))}
                </Loader>
              )}

              {[`results`, `error`].includes(status) && total <= 0 && (
                <Empty>{translations.search.noResults}</Empty>
              )}

              {status == `results` && total > 0 && (
                <Results>
                  {results.map((result) => (
                    <SearchResult
                      key={`${result.type}-${result.id}`}
                      data={result}
                      TitleStyled={Heading3}
                      LabelStyled={SearchResultLabel}
                      DescriptionStyled={Paragraph}
                    />
                  ))}
                </Results>
              )}

              {hasNextPage && (
                <More>
                  <button type="button" onClick={showNextPage}>
                    {translations.search.loadMoreResults}
                  </button>
                </More>
              )}
            </>
          )}
        </Search>
      </Container>
    </Layout>
  )
}

export const Head = ({ query, data: { translations } }) => {
  return (
    <Meta
      data={{
        title: query
          ? `${translations.search.title} “${query}”`
          : `${translations.search.title}`,
        canonical: `/${translations.search.slug}/`,
      }}
    />
  )
}

export const pageQuery = graphql`
  query {
    site {
      siteMetadata {
        name
      }
    }

    translations: translationsJson {
      search {
        title
        slug
        results
        noResults
        loadingResults
        loadMoreResults
      }
    }

    image: file(
      sourceInstanceName: { eq: "images" }
      relativePath: { eq: "content/hiking.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 1920)
      }
    }
  }
`

const Images = styled.div`
  height: 40vh;
  max-height: ${em(360)};
  /* margin-bottom: ${em(30)}; */
  position: relative;

  @media ${({ theme }) => theme.mq.mediumDown} {
    /* margin-bottom: ${em(20)}; */
  }

  &::after {
    content: "";
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.7),
      rgba(0, 0, 0, 0)
    );
  }

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`

const Container = styled.div`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  min-height: 50vh;
  padding-top: ${em(40)};
  padding-bottom: ${em(80)};
`

const SearchFormWrap = styled.div`
  margin-top: ${em(24)};
`

const Info = styled(ParagraphSmall)`
  margin-top: ${em(20)};
  color: ${({ theme }) => theme.colors.forgedsteal};
  text-align: right;
`

const Loader = styled.div`
  margin-top: ${em(30)};
  display: flex;
  flex-direction: column;
  gap: ${em(30)};

  div {
    height: ${em(180)};
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: ${em(15)};
    animation: ${({ theme }) => theme.animations.fadeBlink} 1s
      ${({ theme }) => theme.easings.default} infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  span {
    ${hideVisually()}
  }
`

const Empty = styled(Paragraph)`
  margin-top: ${em(30)};
`

const Results = styled.div`
  margin-top: ${em(50)};
  display: flex;
  flex-direction: column;
  gap: ${em(30)};
`

const More = styled.div`
  padding-top: ${em(20)};
  display: flex;
  justify-content: center;

  button {
    ${({ theme }) => theme.fonts.set(`primary`, `bold`)};

    padding: ${em(10)} ${em(30)};
    background-color: ${({ theme }) => theme.colors.greenAlt};
    color: ${({ theme }) => theme.colors.white};
    border-radius: ${em(15)};
    font-size: ${em(16)};
    text-transform: uppercase;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.5;
    }
  }
`

const SearchResultLabel = styled.span``
